/**
 * The ad groups of one campaign, read and not written.
 *
 * A component rather than more of `Campaigns.tsx` for the reason twads#120 split its
 * line items out: the page already carries a create form and a switch, and a second
 * list with its own paging inside it stops being readable. The sentences are in
 * `lib/adgroup-copy.ts`, where a runner can read them without React.
 *
 * 🔴 **It stops and does not start.** The notice says which on the section itself: an
 * operator hunting for a control that is absent reads the absence as a broken button.
 *
 * Every branch after a stop comes from `stopOutcome` in `lib/ad-groups.ts`, and whether
 * the control is offered again comes from `mayStopAgain` there. Nothing is decided in
 * this file, so the cases that hold those decisions need no browser — and they assert
 * the consequence rather than that a call was made (twads#126).
 *
 * 🔴 **The caller gives this a `key` of the campaign id**, so React remounts it when the
 * campaign changes and the page number resets by construction. An effect that reset it
 * instead read `campaignId` only as a trigger and never used the value — which Biome's
 * `useExhaustiveDependencies` called out, correctly: the dependency was a signal dressed
 * as a read. Page 3 of one campaign's ad groups is not page 3 of another's, and carrying
 * the number over would have shown an empty page where the answer is "none".
 */
import { useCallback, useEffect, useState } from "react";
import { STOP, type StopOutcome, mayStopAgain, stopOutcome } from "../lib/ad-groups.ts";
import {
	FLOOR_BANNER,
	STOP_ONLY,
	budgetText,
	deliveryText,
	emptyText,
	pagerLabel,
	stopBanner,
	switchText,
} from "../lib/adgroup-copy.ts";
import { type AdGroup, type CampaignPage, api } from "../lib/api.ts";
import { Badge, Button, InfoBanner, Table } from "../ui/index.ts";

type Listing = { adGroups: AdGroup[]; page: CampaignPage; truncated: boolean };
type State =
	| { kind: "unasked" }
	| { kind: "loading" }
	| { kind: "ready"; value: Listing }
	| { kind: "failed"; error: string };

export function AdGroups({
	advertiser,
	campaignId,
	campaignName,
}: {
	advertiser: string;
	campaignId: string | null;
	campaignName: string | null;
}) {
	const [state, setState] = useState<State>({ kind: "unasked" });
	const [wanted, setWanted] = useState(1);
	/** Which ad group a stop is in flight for, and what the last one resulted in. */
	const [stopping, setStopping] = useState<string | null>(null);
	const [outcome, setOutcome] = useState<{ id: string; value: StopOutcome } | null>(null);

	const read = useCallback(
		(page: number) => {
			if (!campaignId) {
				setState({ kind: "unasked" });
				return;
			}
			setState({ kind: "loading" });
			api.adGroups(advertiser, campaignId, { page }).then(
				(value) => setState({ kind: "ready", value }),
				(e: unknown) =>
					setState({ kind: "failed", error: e instanceof Error ? e.message : "read failed" }),
			);
		},
		[advertiser, campaignId],
	);

	useEffect(() => {
		read(wanted);
	}, [read, wanted]);

	async function stop(id: string) {
		setStopping(id);
		setOutcome(null);
		try {
			const { status, body } = await api.setAdGroupStatus(id, { advertiser, status: STOP });
			setOutcome({ id, value: stopOutcome(status, body) });
		} catch (e) {
			// A throw is not an answer: the request did not complete, so nothing is known
			// about the ad group. Reported as a refusal with no status of its own.
			setOutcome({
				id,
				value: stopOutcome(0, { error: e instanceof Error ? e.message : "the stop failed" }),
			});
		} finally {
			setStopping(null);
		}
		// Re-read whatever the outcome: the row's switch is what an operator looks at next,
		// and leaving it stale would contradict the banner above it.
		read(wanted);
	}

	if (!campaignId) {
		return (
			<section className="border-t border-line pt-6">
				<h2 className="text-sm font-semibold text-content">Ad groups</h2>
				<p className="mt-1 text-xs text-muted">{emptyText("unasked")}</p>
			</section>
		);
	}

	return (
		<section className="border-t border-line pt-6">
			<h2 className="text-sm font-semibold text-content">
				Ad groups{campaignName ? ` — ${campaignName}` : ""}
			</h2>
			<p className="mt-1 text-xs text-muted">{STOP_ONLY}</p>

			<div className="mt-4 space-y-3">
				{state.kind === "failed" && <InfoBanner variant="danger" title={state.error} />}
				{outcome && (
					<InfoBanner
						variant={outcome.value.kind === "set" ? "info" : "warning"}
						title={stopBanner(outcome.value).title}
					>
						{stopBanner(outcome.value).body}
					</InfoBanner>
				)}
				{state.kind === "ready" && state.value.truncated && (
					<InfoBanner variant="warning" title={FLOOR_BANNER.title}>
						{FLOOR_BANNER.body}
					</InfoBanner>
				)}
			</div>

			<div className="mt-4">
				<Table<AdGroup>
					columns={[
						{
							id: "name",
							header: "Ad group",
							accessor: (g) => (
								<>
									<div className="font-medium text-content">{g.name}</div>
									<div className="font-mono text-xs text-muted">{g.id}</div>
								</>
							),
						},
						{
							id: "switch",
							header: "Switch",
							accessor: (g) => {
								const last = outcome?.id === g.id ? outcome.value : null;
								// Offered when the ad group is on, and withdrawn after an outcome that
								// cannot be improved on. `mayStopAgain` decides, not this file.
								const offer = last ? mayStopAgain(last) : g.operationStatus === "ENABLE";
								return (
									<div className="flex items-center gap-2">
										<Badge
											variant={
												g.operationStatus === "ENABLE"
													? "success"
													: g.operationStatus === "DISABLE"
														? "default"
														: "warning"
											}
										>
											{switchText(g.operationStatus)}
										</Badge>
										{offer && (
											<Button
												variant="ghost"
												size="sm"
												loading={stopping === g.id}
												disabled={stopping !== null}
												onClick={() => void stop(g.id)}
											>
												Stop
											</Button>
										)}
									</div>
								);
							},
						},
						{
							id: "delivery",
							header: "Delivery",
							accessor: (g) => (
								<span className="font-mono text-xs">{deliveryText(g.secondaryStatus)}</span>
							),
						},
						{
							id: "goal",
							header: "Optimization",
							accessor: (g) => (
								<span className="font-mono text-xs">
									{g.optimizationGoal} · {g.billingEvent} · {g.pacing}
								</span>
							),
						},
						{
							id: "budget",
							header: "Budget",
							accessor: (g) => <span className="font-mono text-xs">{budgetText(g)}</span>,
						},
						{
							id: "schedule",
							header: "Starts (UTC)",
							accessor: (g) => <span className="text-muted">{g.scheduleStartTime}</span>,
						},
					]}
					data={state.kind === "ready" ? state.value.adGroups : []}
					keyExtractor={(g) => g.id}
					failed={state.kind === "failed"}
					loading={state.kind === "loading"}
					emptyMessage={emptyText("none")}
				/>
			</div>

			{state.kind === "ready" && state.value.page.totalPages > 1 && (
				<div className="mt-4 flex items-center gap-3">
					<Button
						variant="ghost"
						size="sm"
						disabled={wanted <= 1}
						onClick={() => setWanted((p) => p - 1)}
					>
						Previous
					</Button>
					<span className="text-xs text-muted">{pagerLabel(state.value.page)}</span>
					<Button
						variant="ghost"
						size="sm"
						disabled={wanted >= state.value.page.totalPages}
						onClick={() => setWanted((p) => p + 1)}
					>
						Next
					</Button>
				</div>
			)}
		</section>
	);
}
